"use client";

import { useEffect, useState } from "react";
import { useDisclosure } from "@/utils/useDisclosure";
import {
  BillingLimitParams,
  registerBillingLimitModal,
} from "../billingModalController";
import { LimitHitModal } from "@/components/Modals/BillingUsageLimitModal";
import { BillingTrialLimitModal } from "@/components/Modals/BillingTrialLimitModal";
import { DATA_KEYS, useRemoteData } from "../useRemoteData";
import { registerStorageLimitDialog } from "../storageLimitController";
import { StorageLimitDialog } from "@/components/Storage/StorageLimitDialog";
import {
  storageDialogDismissedLevelAtom,
  useStorageStatus,
} from "@/services/Storage/useStorageStatus";
import { useAtom } from "jotai";

export function PlatformDialogs() {
  const billingLimitModalControls = useDisclosure();
  const [limitParams, setLimitParams] = useState<BillingLimitParams>({});
  const storageDialogControls = useDisclosure();
  const { status: storageStatus } = useStorageStatus();
  const [dismissedStorageLevel, setDismissedStorageLevel] = useAtom(
    storageDialogDismissedLevelAtom
  );
  const { data: billingData, isLoading } = useRemoteData(DATA_KEYS.BILLING);

  const isTrialOver =
    billingData?.trial_end_date &&
    new Date(billingData?.trial_end_date) < new Date();

  const isFreePlan = billingData?.plan?.toLowerCase() === "test";

  const openLimitModal = billingLimitModalControls.onOpen;
  useEffect(() => {
    registerBillingLimitModal((params) => {
      setLimitParams(params);
      openLimitModal();
    });
  }, [openLimitModal]);

  useEffect(() => {
    registerStorageLimitDialog(storageDialogControls.onOpen);
  }, [storageDialogControls.onOpen]);

  // Raised by a refused request, and by the poll finding a level nobody has
  // acknowledged yet: someone opening the app on a full disk should be told
  // before they try to run something. Derived rather than an effect - closing
  // always records the level, so "not dismissed at this level" is the whole
  // condition.
  const storageLevel = storageStatus?.level;
  const storageNeedsNotice =
    !!storageLevel &&
    storageLevel !== "healthy" &&
    dismissedStorageLevel !== storageLevel;
  const closeStorageDialog = () => {
    if (storageLevel) setDismissedStorageLevel(storageLevel);
    storageDialogControls.onClose();
  };

  return (
    <>
      {!isLoading &&
        (isTrialOver && isFreePlan ? (
          <BillingTrialLimitModal
            isOpen={billingLimitModalControls.isOpen}
            onClose={billingLimitModalControls.onClose}
          />
        ) : (
          billingLimitModalControls.isOpen && (
            <LimitHitModal
              isOpen={billingLimitModalControls.isOpen}
              onClose={billingLimitModalControls.onClose}
              params={limitParams}
            />
          )
        ))}

      {/* Raised by the 507 interceptor, so it must be mountable at any time. */}
      <StorageLimitDialog
        isOpen={storageDialogControls.isOpen || storageNeedsNotice}
        onClose={closeStorageDialog}
      />
    </>
  );
}

export default PlatformDialogs;
